import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";

const FAQS = [
  {
    q: "How do I receive the ebook after purchase?",
    a: "Immediately. Right after checkout, Hotmart sends your access by email and you can download the PDF instantly — it works on your phone, tablet and computer, and it's yours forever.",
  },
  {
    q: "Do I need special or hard-to-find ingredients?",
    a: "No. Every recipe uses ingredients you'll find in a normal grocery store — green tea, mint, ginger, lemon, hibiscus, rooibos, chamomile, cinnamon and a few more pantry staples.",
  },
  {
    q: "Is this another restrictive diet?",
    a: "The opposite. The method is built around satisfaction, not restriction: balanced meals, gentle habits, self-trust and consistency over perfection. No crash diets, no forbidden foods.",
  },
  {
    q: "What about caffeine? Is it safe?",
    a: "The guide has a full chapter on green tea, herbal tea and caffeine, plus safe brewing and mindful drinking practices — including caffeine-free options for evenings.",
  },
  {
    q: "What if it doesn't work for me?",
    a: "You're covered by a 30-day money-back guarantee. If the guide isn't for you, request a refund right from your Hotmart account within 30 days — it takes two clicks, and you get 100% of your purchase back.",
  },
];

export default function FAQ() {
  const [open, setOpen] = useState(0);

  return (
    <section id="faq" className="py-16 sm:py-24 lg:py-32" data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-12"
        >
          <p className="text-xs sm:text-sm font-bold tracking-[0.24em] uppercase text-[#D4A359] mb-4">
            Questions, answered
          </p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#1A241E] leading-tight" data-testid="faq-heading">
            Frequently asked questions
          </h2>
        </motion.div>

        <div className="space-y-3" data-testid="faq-list">
          {FAQS.map((item, i) => {
            const isOpen = open === i;
            return (
              <motion.div
                key={item.q}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.07, duration: 0.5 }}
                className={`rounded-xl border transition-colors duration-400 ${
                  isOpen ? "bg-white border-[#D4A359]/60 shadow-md" : "bg-white/70 border-[#1C3A27]/10"
                }`}
              >
                <button
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  className="w-full flex items-center justify-between gap-4 px-5 sm:px-6 py-4 text-left"
                  data-testid={`faq-question-${i}`}
                >
                  <span className="text-sm sm:text-base font-bold text-[#1A241E]">{item.q}</span>
                  <Plus
                    className={`w-5 h-5 text-[#1C3A27] shrink-0 transition-transform duration-400 ${isOpen ? "rotate-45" : ""}`}
                  />
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                    >
                      <p className="px-5 sm:px-6 pb-5 text-sm sm:text-base text-[#5C6A60] leading-relaxed" data-testid={`faq-answer-${i}`}>
                        {item.a}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
